/**
 * AdminSportMemoryPage — /admin/memoire-sport (V12.x)
 *
 * CRUD basique + vérification des liens externes pour `sport_memory_items`.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, RefreshCw, ExternalLink, ArrowLeft, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

const CONTENT_TYPES = ["ARCHIVE_VIDEO", "ARCHIVE_AUDIO", "ARCHIVE_PHOTO", "ARTICLE_HISTORIQUE",
  "LIVRE", "THESE", "ETUDE", "RESULTATS", "PALMARES", "TEMOIGNAGE", "EVENEMENT", "PORTRAIT",
  "DOCUMENT_OFFICIEL", "CATALOGUE_ARCHIVES", "HISTOIRE_INSTITUTIONNELLE"];
const MEDIA_TYPES = ["VIDEO", "PDF", "WEBPAGE", "BOOK", "ARCHIVE_CATALOG", "NONE"];

const EMPTY = {
  title: "", subtitle: "", description_short: "", description_long: "",
  content_type: "ARTICLE_HISTORIQUE", media_type: "WEBPAGE",
  year_start: "", year_end: "",
  sport: "", event: "",
  source_name: "", source_url: "", media_url: "", document_url: "",
  author: "", publisher: "", publication_year: "",
  historical_importance: 3, editorial_priority: 50,
  featured: false, verified: true, visible: true,
  tags: "",
};

function EditDialog({ open, onOpenChange, initial, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const isEdit = !!initial?.id;
  useEffect(() => {
    if (open) {
      setForm({
        ...EMPTY,
        ...(initial || {}),
        year_start: initial?.year_start ?? "",
        year_end: initial?.year_end ?? "",
        publication_year: initial?.publication_year ?? "",
        historical_importance: initial?.historical_importance ?? 3,
        editorial_priority: initial?.editorial_priority ?? 50,
        tags: (initial?.tags || []).join(", "),
      });
    }
  }, [open, initial]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));
  const submit = async () => {
    if (!form.title.trim()) { toast.error("Titre requis"); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        year_start: form.year_start ? Number(form.year_start) : null,
        year_end: form.year_end ? Number(form.year_end) : null,
        publication_year: form.publication_year ? Number(form.publication_year) : null,
        historical_importance: Number(form.historical_importance) || 3,
        editorial_priority: Number(form.editorial_priority) || 50,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      };
      if (isEdit) await api.patch(`/sport-memory/${initial.id}`, payload);
      else await api.post("/sport-memory", payload);
      toast.success(isEdit ? "Fiche mise à jour" : "Fiche créée");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'enregistrement");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Modifier la fiche" : "Nouvelle fiche patrimoniale"}</DialogTitle>
          <DialogDescription className="text-xs">Toutes les URL doivent être exactes — ne pas inventer d'URL.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 py-2">
          <div className="md:col-span-2"><Label>Titre *</Label><Input value={form.title} onChange={set("title")} /></div>
          <div className="md:col-span-2"><Label>Sous-titre</Label><Input value={form.subtitle} onChange={set("subtitle")} /></div>
          <div className="md:col-span-2"><Label>Description courte</Label><Textarea rows={2} value={form.description_short} onChange={set("description_short")} /></div>
          <div><Label>Content type</Label>
            <Select value={form.content_type} onValueChange={(v) => setForm((f) => ({ ...f, content_type: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{CONTENT_TYPES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Media type</Label>
            <Select value={form.media_type} onValueChange={(v) => setForm((f) => ({ ...f, media_type: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{MEDIA_TYPES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Année début</Label><Input type="number" value={form.year_start} onChange={set("year_start")} /></div>
          <div><Label>Année fin</Label><Input type="number" value={form.year_end} onChange={set("year_end")} /></div>
          <div><Label>Discipline</Label><Input value={form.sport} onChange={set("sport")} /></div>
          <div><Label>Événement</Label><Input value={form.event} onChange={set("event")} /></div>
          <div className="md:col-span-2"><Label>Source (nom)</Label><Input value={form.source_name} onChange={set("source_name")} /></div>
          <div className="md:col-span-2"><Label>URL source</Label><Input type="url" value={form.source_url} onChange={set("source_url")} /></div>
          <div className="md:col-span-2"><Label>URL média (vidéo/audio direct)</Label><Input type="url" value={form.media_url} onChange={set("media_url")} /></div>
          <div className="md:col-span-2"><Label>URL document (PDF)</Label><Input type="url" value={form.document_url} onChange={set("document_url")} /></div>
          <div><Label>Auteur</Label><Input value={form.author} onChange={set("author")} /></div>
          <div><Label>Éditeur</Label><Input value={form.publisher} onChange={set("publisher")} /></div>
          <div><Label>Année publication</Label><Input type="number" value={form.publication_year} onChange={set("publication_year")} /></div>
          <div><Label>Importance historique (1-5)</Label><Input type="number" min="1" max="5" value={form.historical_importance} onChange={set("historical_importance")} /></div>
          <div><Label>Priorité éditoriale (1-100)</Label><Input type="number" min="1" max="100" value={form.editorial_priority} onChange={set("editorial_priority")} /></div>
          <div className="md:col-span-2"><Label>Tags (séparés par virgules)</Label><Input value={form.tags} onChange={set("tags")} /></div>
          <div className="md:col-span-2 flex flex-wrap gap-4 pt-2">
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={form.featured} onCheckedChange={(v) => setForm((f) => ({ ...f, featured: v === true }))} /> Incontournable</label>
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={form.verified} onCheckedChange={(v) => setForm((f) => ({ ...f, verified: v === true }))} /> Vérifiée</label>
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={form.visible} onCheckedChange={(v) => setForm((f) => ({ ...f, visible: v === true }))} /> Visible</label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Annuler</Button>
          <Button onClick={submit} disabled={busy}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (isEdit ? "Enregistrer" : "Créer")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminSportMemoryPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [checkingId, setCheckingId] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/sport-memory", { params: { limit: 200 } })
      .then((r) => setItems(r.data.items || []))
      .catch(() => toast.error("Impossible de charger les fiches"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const qq = q.toLowerCase();
    return items.filter((it) =>
      (it.title || "").toLowerCase().includes(qq) ||
      (it.author || "").toLowerCase().includes(qq) ||
      (it.source_name || "").toLowerCase().includes(qq) ||
      (it.tags || []).some((t) => (t || "").toLowerCase().includes(qq))
    );
  }, [items, q]);

  const handleCheckOne = async (id) => {
    setCheckingId(id);
    try {
      const { data } = await api.post(`/sport-memory/${id}/check-link`);
      toast.success(`Lien : ${data.link_status}${data.http_status ? ` (${data.http_status})` : ""}`);
      load();
    } catch { toast.error("Échec vérif"); } finally { setCheckingId(null); }
  };
  const handleBulkCheck = async () => {
    setBulkBusy(true);
    try {
      const { data } = await api.post("/sport-memory/bulk-check-links");
      toast.success(`Vérifiés : ${data.checked} — OK ${data.stats.ok} · À revoir ${data.stats.to_review}`);
      load();
    } catch { toast.error("Échec vérif bulk"); } finally { setBulkBusy(false); }
  };
  const handleDelete = async (id, title) => {
    if (!confirm(`Supprimer « ${title} » ?`)) return;
    try { await api.delete(`/sport-memory/${id}`); toast.success("Supprimé"); load(); }
    catch { toast.error("Échec"); }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
      <Link to="/admin" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-3">
        <ArrowLeft className="h-3 w-3" /> Admin
      </Link>
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <div>
          <h1 className="font-display font-bold text-2xl">Mémoire du Sport — Admin</h1>
          <p className="text-sm text-muted-foreground">{items.length} fiche{items.length > 1 ? "s" : ""} · gérez la bibliothèque patrimoniale.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={handleBulkCheck} disabled={bulkBusy} data-testid="admin-bulk-check">
            {bulkBusy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
            Vérifier tous les liens
          </Button>
          <Button size="sm" onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="admin-new-sport-memory">
            <Plus className="h-4 w-4 mr-1" /> Nouvelle fiche
          </Button>
        </div>
      </div>
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un titre, auteur, source…" className="mb-4 max-w-md" data-testid="admin-search" />
      {loading ? (
        <Card className="p-6 text-center text-sm text-muted-foreground">Chargement…</Card>
      ) : (
        <div className="border rounded-md overflow-x-auto bg-card">
          <table className="w-full text-sm">
            <thead className="bg-secondary/50 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-3 py-2">Titre</th>
                <th className="text-left px-3 py-2">Année</th>
                <th className="text-left px-3 py-2">Type</th>
                <th className="text-left px-3 py-2">Source</th>
                <th className="text-left px-3 py-2">Prio</th>
                <th className="text-left px-3 py-2">État</th>
                <th className="text-left px-3 py-2">Lien</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filtered.map((it) => (
                <tr key={it.id} data-testid={`admin-row-${it.slug}`}>
                  <td className="px-3 py-2 max-w-md">
                    <div className="font-semibold leading-tight">{it.title}</div>
                    {it.author && <div className="text-xs text-muted-foreground">{it.author}</div>}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{it.year_start || "—"}{it.year_end && it.year_end !== it.year_start ? `–${it.year_end}` : ""}</td>
                  <td className="px-3 py-2 text-xs">{it.content_type}<br /><span className="text-muted-foreground">{it.media_type}</span></td>
                  <td className="px-3 py-2 text-xs">{it.source_name || "—"}</td>
                  <td className="px-3 py-2 text-xs font-mono">{it.editorial_priority}</td>
                  <td className="px-3 py-2 text-xs">
                    {it.featured && <Badge className="mr-1 bg-[hsl(var(--aito-gold))] text-black text-[9px]">★</Badge>}
                    {!it.visible && <Badge variant="outline" className="text-[9px]">masqué</Badge>}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {it.link_status === "ok" && <Badge variant="outline" className="text-[9px] bg-green-50 text-green-800 border-green-200">OK</Badge>}
                    {it.link_status === "to_review" && <Badge variant="outline" className="text-[9px] bg-yellow-50 text-yellow-800 border-yellow-200">À revoir</Badge>}
                    {!it.link_status && <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => handleCheckOne(it.id)} disabled={checkingId === it.id} title="Vérifier le lien">
                        {checkingId === it.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                      </Button>
                      {(it.media_url || it.source_url || it.document_url) && (
                        <Button size="sm" variant="ghost" asChild>
                          <a href={it.media_url || it.document_url || it.source_url} target="_blank" rel="noreferrer" title="Ouvrir la source">
                            <ExternalLink className="h-3.5 w-3.5" />
                          </a>
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => { setEditing(it); setDialogOpen(true); }} title="Éditer">
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(it.id, it.title)} title="Supprimer">
                        <Trash2 className="h-3.5 w-3.5 text-red-700" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="text-center py-6 text-muted-foreground text-sm">Aucun résultat</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      <EditDialog open={dialogOpen} onOpenChange={setDialogOpen} initial={editing} onSaved={load} />
    </div>
  );
}
